import { useState } from 'react';
import type { RrcLocationProfile } from '@ots-frontend/ots-data';
import {
  OtsForm,
  OtsFormActions,
  OtsFormColumns,
  OtsFormErrorSummary,
  OtsFormRow,
  OtsFormSection,
} from '../../components/OtsForm';
import { OtsTextInput } from '../../components/OtsTextInput';
import { OtsSelect, type OtsSelectOption } from '../../components/OtsSelect';
import { OtsDateTimePicker } from '../../components/OtsDateTimePicker';
import { OtsInlineAlert } from '../../components/OtsInlineAlert';
import { OtsPersonSelect } from '../../select/OtsEntitySelects';
import { OtsLocationSelect } from '../../select/OtsLocationSelect';
import {
  locationProfileOptions,
  toLocationProfileDraft,
  toLocationProfilePayload,
  validateLocationProfile,
  type LocationProfileDraft,
  type LocationProfileErrors,
} from './rrc-location-profile-ui';

export type RrcLocationProfileFormProps = {
  profile?: Partial<RrcLocationProfile>;
  mode?: 'create' | 'edit' | 'copy';
  locationLocked?: boolean;
  regionId?: string;
  locationOptions?: OtsSelectOption[];
  personOptions?: OtsSelectOption[];
  compact?: boolean;
  submitting?: boolean;
  error?: string;
  onSubmit: (profile: Partial<RrcLocationProfile>) => void;
  onCancel?: () => void;
};

export const RrcLocationProfileForm = ({
  profile,
  mode = 'create',
  locationLocked,
  regionId,
  locationOptions,
  personOptions,
  compact,
  submitting,
  error,
  onSubmit,
  onCancel,
}: RrcLocationProfileFormProps) => {
  const [draft, setDraft] = useState(() => toLocationProfileDraft(profile));
  const [errors, setErrors] = useState<LocationProfileErrors>({});
  const [attempted, setAttempted] = useState(false);
  const size = compact ? 'compact' : 'default';
  const options = locationProfileOptions();
  const change = (patch: Partial<LocationProfileDraft>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    if (attempted) setErrors(validateLocationProfile(next));
  };
  const submit = () => {
    if (submitting) return;
    const next = validateLocationProfile(draft);
    setErrors(next);
    setAttempted(true);
    if (!Object.keys(next).length)
      onSubmit(
        toLocationProfilePayload(
          draft,
          mode === 'edit' ? profile?.id : undefined
        )
      );
  };
  const input = (
    key:
      | 'facilityName'
      | 'facilityPermitNumber'
      | 'leaseName'
      | 'leaseNumber'
      | 'gasPlantNumber'
      | 'latCoord'
      | 'longCoord'
      | 'contactName'
      | 'contactPhone'
      | 'contactEmail',
    label: string,
    required = false
  ) => (
    <OtsTextInput
      label={label}
      required={required}
      value={draft[key]}
      error={errors[key]}
      size={size}
      onChange={(event) => change({ [key]: event.currentTarget.value })}
      type={key === 'contactEmail' ? 'email' : 'text'}
      inputMode={
        key === 'latCoord' || key === 'longCoord' ? 'decimal' : undefined
      }
    />
  );
  return (
    <OtsForm
      aria-label={
        mode === 'edit'
          ? 'Edit location profile'
          : mode === 'copy'
          ? 'Copy location profile'
          : 'New location profile'
      }
      noValidate
      density={size}
      disabled={submitting}
      onSubmit={submit}
      alert={
        <>
          {error && (
            <OtsInlineAlert tone="danger" title="Profile could not be saved">
              {error}
            </OtsInlineAlert>
          )}
          {attempted && <OtsFormErrorSummary errors={errors} />}
        </>
      }
      actions={
        <OtsFormActions
          onCancel={onCancel}
          submitting={submitting}
          submitLabel={
            mode === 'edit'
              ? 'Save changes'
              : mode === 'copy'
              ? 'Create version'
              : 'Create profile'
          }
          submittingLabel="Saving…"
        />
      }
    >
      <OtsFormColumns>
        <div className="space-y-4">
          <OtsFormSection
            title="Location & facility"
            description="The location and district identify a profile series and cannot change during an edit."
          >
            <OtsLocationSelect
              locationType={draft.locationType}
              value={draft.locationId}
              locationName={profile?.locationName}
              onChange={(locationId, locationType) =>
                change({ locationId: locationId ?? '', locationType })
              }
              disabled={mode === 'edit' || locationLocked}
              regionId={regionId}
              options={locationOptions}
              size={size}
              error={errors.locationId ?? errors.locationType}
            />
            <OtsSelect
              label="RRC district"
              required
              options={options.districts}
              value={draft.rrcDistrict}
              disabled={mode === 'edit'}
              onChange={(rrcDistrict) =>
                change({ rrcDistrict: rrcDistrict ?? '' })
              }
              error={errors.rrcDistrict}
              size={size}
              placeholder="Select a district"
            />
            {input('facilityName', 'Facility name', true)}
            <OtsFormRow>
              {input('facilityPermitNumber', 'Facility permit number')}
              {input('gasPlantNumber', 'Gas plant number')}
            </OtsFormRow>
            <OtsSelect
              label="Commercial"
              value={draft.commercial}
              size={size}
              options={[
                { value: 'unknown', label: 'Not specified' },
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
              ]}
              onChange={(commercial) =>
                change({ commercial: commercial ?? 'unknown' })
              }
            />
          </OtsFormSection>
          {mode === 'copy' && (
            <OtsInlineAlert title="Choose the destination series" size={size}>
              Keeping the same location and district adds a version to that
              series. Changing either creates a separate series.
            </OtsInlineAlert>
          )}
          <OtsFormSection
            title="Effective dates"
            description="The server maintains version history and checks for overlapping effective periods."
          >
            <OtsFormRow>
              <OtsDateTimePicker
                label="Effective start"
                required
                value={draft.start}
                onChange={(start) => change({ start })}
                error={errors.start}
                size={size}
              />
              <OtsDateTimePicker
                label="Effective end"
                value={draft.end}
                onChange={(end) => change({ end })}
                error={errors.end}
                size={size}
                description="Leave blank for no end date."
              />
            </OtsFormRow>
            {mode !== 'create' && (
              <OtsInlineAlert title="Version history is preserved" size={size}>
                Saving an effective profile may create a replacement version.
                Existing manifests keep their recorded profile snapshot.
              </OtsInlineAlert>
            )}
          </OtsFormSection>
          <OtsFormSection
            title="Automatic certification"
            description="Configure EP-9 and EP-10 signing independently."
          >
            {(['autoEp9', 'autoEp10'] as const).map((form) => (
              <OtsFormRow key={form}>
                <OtsSelect
                  label={`${
                    form === 'autoEp9' ? 'EP-9' : 'EP-10'
                  } signer strategy`}
                  value={draft[`${form}SignerStrategy`]}
                  options={options.strategies}
                  placeholder="Not configured"
                  size={size}
                  onChange={(strategy) =>
                    change({ [`${form}SignerStrategy`]: strategy ?? '' })
                  }
                />
                <div>
                  <OtsPersonSelect
                    label={`${form === 'autoEp9' ? 'EP-9' : 'EP-10'} signer`}
                    selectedOptions={
                      profile?.[`${form}SignerId`]
                        ? [
                            {
                              value: profile[`${form}SignerId`]!,
                              label:
                                profile[`${form}SignerName`] ??
                                profile[`${form}SignerId`]!,
                            },
                          ]
                        : undefined
                    }
                    regionId={regionId}
                    options={personOptions}
                    value={draft[`${form}SignerId`]}
                    onChange={(id) => change({ [`${form}SignerId`]: id ?? '' })}
                    disabled={draft[`${form}SignerStrategy`] !== 'SELECTED'}
                    size={size}
                  />
                  {errors[`${form}SignerId`] && (
                    <p
                      role="alert"
                      className="mt-1 text-xs text-[var(--ots-error)]"
                    >
                      {errors[`${form}SignerId`]}
                    </p>
                  )}
                </div>
              </OtsFormRow>
            ))}
          </OtsFormSection>
        </div>
        <div className="space-y-4">
          <OtsFormSection
            title="Lease details"
            description="Record lease identifiers when they apply to this location."
          >
            {input('leaseName', 'Lease name')}
            {input('leaseNumber', 'Lease number')}
          </OtsFormSection>
          <OtsFormSection
            title="Coordinates"
            description="Enter both coordinates in decimal degrees, or leave both blank."
          >
            <OtsFormRow>
              {input('latCoord', 'Latitude')}
              {input('longCoord', 'Longitude')}
            </OtsFormRow>
          </OtsFormSection>
          <OtsFormSection title="Contact">
            {input('contactName', 'Contact name')}
            <OtsFormRow>
              {input('contactPhone', 'Contact phone')}
              {input('contactEmail', 'Contact email')}
            </OtsFormRow>
          </OtsFormSection>
          <OtsFormSection title="Address">
            {(
              [
                'street',
                'street2',
                'city',
                'county',
                'state',
                'province',
                'postalCode',
                'country',
              ] as const
            ).map((key) => (
              <OtsTextInput
                key={key}
                label={
                  {
                    street: 'Street',
                    street2: 'Address line 2',
                    city: 'City',
                    county: 'County',
                    state: 'State',
                    province: 'Province',
                    postalCode: 'Postal code',
                    country: 'Country',
                  }[key]
                }
                value={draft.address[key] ?? ''}
                onChange={(event) =>
                  change({
                    address: {
                      ...draft.address,
                      [key]: event.currentTarget.value,
                    },
                  })
                }
                size={size}
              />
            ))}
          </OtsFormSection>
        </div>
      </OtsFormColumns>
    </OtsForm>
  );
};
